import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { snipe } from '../commands/withdraw'; // Import the snipe function

interface SnipingOpportunity {
    id: number;
    network: 'solana' | 'ethereum';
    price: number; // Price in SOL/ETH
    quantity: number; // Quantity in SOL/ETH
    expiresAt: number; // Timestamp for expiration
}

const Snipping: React.FC = () => {
    const [opportunities, setOpportunities] = useState<SnipingOpportunity[]>([]);
    const [isSnipeLoading, setIsSnipeLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    useEffect(() => {
        const generateRandomOpportunity = () => {
            const randomNetwork = Math.random() < 0.5 ? 'solana' : 'ethereum';
            const randomPrice = (Math.random() * (1 - 0.10) + 0.10).toFixed(4);
            const randomQuantity = (Math.random() * (8 - 1) + 1).toFixed(2);
            const id = Math.floor(Math.random() * 10000);
            const expiresAt = Date.now() + 2 * 60 * 1000; // 2 mins

            setOpportunities((prev) => [
                {
                    id,
                    network: randomNetwork,
                    price: parseFloat(randomPrice),
                    quantity: parseFloat(randomQuantity),
                    expiresAt,
                },
                ...prev,
            ]);
        };

        const interval = setInterval(generateRandomOpportunity, Math.random() * (40000 - 5000) + 5000);
        return () => clearInterval(interval);
    }, []);

    const handleSnipe = async (opportunity: SnipingOpportunity) => {
        if (!opportunity) return;

        setIsSnipeLoading(true);
        setError(null);

        try {
            const result = await snipe(opportunity.network, opportunity.price);

            if (!result.success) {
                setError(result.message);
                setSuccess(null);
            } else {
                setSuccess(result.message);
                setError(null);
            }
        } catch (err) {
            setError('An error occurred while processing the snipe. Please try again.');
            setSuccess(null);
        } finally {
            setIsSnipeLoading(false);
        }
    };

    useEffect(() => {
        const countdownInterval = setInterval(() => {
            setOpportunities((prev) =>
                prev.map((opportunity) => ({
                    ...opportunity,
                    expiresAt: opportunity.expiresAt - 1000,
                }))
            );
        }, 1000);

        return () => clearInterval(countdownInterval);
    }, []);

    const isExpired = (expiresAt: number) => Date.now() > expiresAt;

    const calculateTimeRemaining = (expiresAt: number) => {
        const remainingTime = Math.max(expiresAt - Date.now(), 0);
        const minutesRemaining = Math.floor(remainingTime / 60000);
        const secondsRemaining = Math.floor((remainingTime % 60000) / 1000);
        return { minutesRemaining, secondsRemaining };
    };

    useEffect(() => {
        if (error) {
            const timer = setTimeout(() => setError(null), 3000);
            return () => clearTimeout(timer);
        }
    }, [error]);

    useEffect(() => {
        if (success) {
            const timer = setTimeout(() => setSuccess(null), 3000);
            return () => clearTimeout(timer);
        }
    }, [success]);

    return (
        <div className="w-full max-w-5xl bg-gradient-to-br from-[#050505] via-[#0a0a0a] to-[#0f0f0f] rounded-2xl shadow-2xl border border-green-600 p-4 sm:p-6 flex flex-col h-[312px]">
            <div className="text-center text-lg sm:text-xl font-mono text-green-400 py-2 sm:py-4 border-b border-green-700 uppercase tracking-widest glow">
                AI Sniping
            </div>

            <div className="space-y-6 text-sm font-mono text-gray-300 flex-1 overflow-hidden">
                <div className="overflow-y-auto h-full">
                    <AnimatePresence initial={false}>
                        {opportunities.map((opportunity) => {
                            const { minutesRemaining, secondsRemaining } = calculateTimeRemaining(opportunity.expiresAt);
                            const label = opportunity.network === 'solana' ? 'SOL' : 'ETH';
                            return (
                                <motion.div
                                    key={opportunity.id}
                                    className="bg-gradient-to-br from-[#050505] via-[#0a0a0a] to-[#0f0f0f] rounded-lg p-4 text-white shadow-lg flex justify-between items-center"
                                    initial={{ opacity: 0, y: -20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 20 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <div className="flex-1">
                                        <div className="text-base font-bold text-green-400">
                                            [{label.toUpperCase()}] ID: {opportunity.id} | SNIPE {opportunity.quantity} {label} for {opportunity.price} {label}
                                        </div>
                                        <div className="text-sm text-gray-400">
                                            Time Left: {minutesRemaining}m {secondsRemaining}s
                                        </div>
                                    </div>
                                    <div>
                                        <button
                                            onClick={() => handleSnipe(opportunity)}
                                            className={`text-white px-2 py-2 rounded-lg shadow-lg ml-0.5 ${isSnipeLoading || isExpired(opportunity.expiresAt) ? 'bg-red-600 opacity-70 cursor-not-allowed' : 'bg-green-600'}`}
                                            disabled={isSnipeLoading || isExpired(opportunity.expiresAt)}
                                        >
                                            {isSnipeLoading ? 'Sniping...' : isExpired(opportunity.expiresAt) ? 'Expired' : 'Snipe'}
                                        </button>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </AnimatePresence>

                    {opportunities.length === 0 && (
                        <div className="mt-2 text-gray-500">No sniping opportunities available at the moment.</div>
                    )}
                </div>
            </div>

            {error && (
                <motion.div
                    className="mt-4 p-2 bg-red-900 text-white rounded-lg text-center text-sm shadow-md"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 0.85 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                >
                    {error}
                </motion.div>
            )}
            {success && (
                <motion.div
                    className="mt-4 p-2 bg-green-900 text-white rounded-lg text-center text-sm shadow-md"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 0.85 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                >
                    {success}
                </motion.div>
            )}
        </div>
    );
};

export default Snipping;
